import { z } from 'zod';
import { DAYS_OF_WEEK } from '../constants/index.ts';

// Time slot schema (HH:MM format)
const timeSlotSchema = z.object({
  open: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Formato inválido (HH:MM)'),
  close: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Formato inválido (HH:MM)'),
});

// Days of week enum
const dayOfWeekSchema = z.enum([
  DAYS_OF_WEEK.MONDAY,
  DAYS_OF_WEEK.TUESDAY,
  DAYS_OF_WEEK.WEDNESDAY,
  DAYS_OF_WEEK.THURSDAY,
  DAYS_OF_WEEK.FRIDAY,
  DAYS_OF_WEEK.SATURDAY,
  DAYS_OF_WEEK.SUNDAY,
]);

// Date string schema for LTO (YYYY-MM-DD format)
const dateStringSchema = z.string().regex(
  /^\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])$/,
  'Formato inválido (YYYY-MM-DD)'
);

// Product availability schema
export const productAvailabilitySchema = z.object({
  // Simple mode: specific days when available
  daysOfWeek: z.array(dayOfWeekSchema).optional(),
  // Advanced mode: specific time slots per day
  schedule: z.record(
    dayOfWeekSchema,
    z.array(timeSlotSchema).nullable()
  ).optional(),
  // Limited Time Offer (LTO) date range
  startDate: dateStringSchema.optional(),
  endDate: dateStringSchema.optional(),
}).nullable();

// Menu item option schema (legacy JSON format)
export const menuItemOptionSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(100),
  price: z.number().min(0),
});

// Menu item option group schema (legacy JSON format)
export const menuItemOptionGroupSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(100),
  required: z.boolean().default(false),
  maxSelections: z.number().int().min(1).default(1),
  // Number of selections that are free (cheapest N are free)
  freeSelections: z.number().int().min(0).optional(),
  options: z.array(menuItemOptionSchema),
});

// Create menu item schema
export const createMenuItemSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  price: z.number().min(0),
  imageUrl: z.string().url().nullable().optional(),
  available: z.boolean().default(true),
  showInMenu: z.boolean().default(true),
  canBeModifier: z.boolean().default(false),
  optionGroups: z.array(menuItemOptionGroupSchema).optional(),
  availability: productAvailabilitySchema.optional(),
  categoryId: z.string(),
  sortOrder: z.number().int().min(0).optional(),
});

// Update menu item schema
export const updateMenuItemSchema = createMenuItemSchema.partial();

// Create menu category schema
export const createMenuCategorySchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  sortOrder: z.number().int().min(0).optional(),
});

// Update menu category schema
export const updateMenuCategorySchema = createMenuCategorySchema.partial();

// Modifier group schemas (relational system)
export const createModifierGroupSchema = z.object({
  name: z.string().min(1).max(100),
  required: z.boolean().default(false),
  minSelections: z.number().int().min(0).default(0),
  maxSelections: z.number().int().min(1).default(1),
  // Number of selections that are free (cheapest N are free)
  freeSelections: z.number().int().min(0).optional(),
  linkedCategoryId: z.string().optional(),
  categoryPriceOverride: z.number().min(0).optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const updateModifierGroupSchema = createModifierGroupSchema.partial();

// Modifier group item schemas
export const createModifierGroupItemSchema = z.object({
  modifierGroupId: z.string(),
  menuItemId: z.string(),
  priceOverride: z.number().min(0).optional(),
  excluded: z.boolean().default(false),
  sortOrder: z.number().int().min(0).optional(),
});

export const updateModifierGroupItemSchema = createModifierGroupItemSchema.partial();

// Menu item modifier group assignment schema
export const assignModifierGroupSchema = z.object({
  menuItemId: z.string(),
  modifierGroupId: z.string(),
  sortOrder: z.number().int().min(0).optional(),
});

// ═══════════════════════════════════════════════════════════════════════════════
// AI Import Schemas
// ═══════════════════════════════════════════════════════════════════════════════

// Schema for an imported category from AI
export const aiImportCategorySchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
});

// Schema for an imported item from AI
// Note: price can be -1 when AI couldn't read/determine the price (sentinel value)
export const aiImportItemSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  price: z.number().refine(p => p >= 0 || p === -1, { message: 'Price must be >= 0 or -1 (unknown)' }),
  categoryName: z.string().min(1).max(100), // Reference to category by name
});

// Schema for the complete AI import result
export const aiImportResultSchema = z.object({
  categories: z.array(aiImportCategorySchema),
  items: z.array(aiImportItemSchema),
});

// Schema for import preview with merge info
// Note: price can be -1 when AI couldn't read/determine the price (sentinel value)
// Frontend should prompt user to enter price manually when price === -1
export const aiImportPreviewItemSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  price: z.number(), // -1 indicates unknown price that needs manual entry
  categoryName: z.string(),
  action: z.enum(['create', 'update', 'skip']),
  existingId: z.string().optional(), // If updating existing item
  changes: z.array(z.string()).optional(), // List of what changed (for updates)
});

export const aiImportPreviewCategorySchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  action: z.enum(['create', 'skip']),
  existingId: z.string().optional(),
});

export const aiImportPreviewSchema = z.object({
  categories: z.array(aiImportPreviewCategorySchema),
  items: z.array(aiImportPreviewItemSchema),
  summary: z.object({
    categoriesToCreate: z.number(),
    categoriesToSkip: z.number(),
    itemsToCreate: z.number(),
    itemsToUpdate: z.number(),
    itemsToSkip: z.number(),
  }),
});

// Request schema for text import
export const aiImportTextRequestSchema = z.object({
  text: z.string().min(10).max(50000),
});

// Request schema for confirming import
export const aiImportConfirmRequestSchema = z.object({
  categories: z.array(z.object({
    name: z.string(),
    description: z.string().optional(),
    action: z.enum(['create', 'skip']),
    existingId: z.string().optional(),
  })),
  items: z.array(z.object({
    name: z.string(),
    description: z.string().optional(),
    price: z.number(),
    categoryName: z.string(),
    action: z.enum(['create', 'update', 'skip']),
    existingId: z.string().optional(),
  })),
});

// ═══════════════════════════════════════════════════════════════════════════════
// JSON Menu Import Schemas (hierarchical bulk import)
// ═══════════════════════════════════════════════════════════════════════════════

// Schema for a modifier group item reference in JSON import
export const jsonImportModifierGroupItemSchema = z.object({
  name: z.string().min(1).max(100), // Reference to item by name
  priceOverride: z.number().min(0).optional(),
});

// Schema for an assignTo entry with optional per-product overrides
export const jsonImportAssignToEntrySchema = z.union([
  z.string().min(1), // Simple: just the product name
  z.object({         // With overrides: product name + selection limits
    name: z.string().min(1),
    requiredOverride: z.boolean().optional(),
    minSelectionsOverride: z.number().int().min(0).optional(),
    maxSelectionsOverride: z.number().int().min(1).optional(),
    freeSelectionsOverride: z.number().int().min(0).optional(),
  }),
]);

// Schema for a modifier group in JSON import
export const jsonImportModifierGroupSchema = z.object({
  name: z.string().min(1).max(100),
  required: z.boolean().default(false),
  minSelections: z.number().int().min(0).default(0),
  maxSelections: z.number().int().min(1).default(1),
  freeSelections: z.number().int().min(0).optional(),
  items: z.array(jsonImportModifierGroupItemSchema).min(1),
  assignTo: z.array(jsonImportAssignToEntrySchema).min(1), // References to items by name, with optional overrides
});

// Schema for an item in JSON import (richer than AI import)
export const jsonImportItemSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  price: z.number().min(0).default(0),
  category: z.string().min(1).max(100), // Reference to category by name
  showInMenu: z.boolean().default(true),
  canBeModifier: z.boolean().default(false),
  availability: productAvailabilitySchema.optional(),
});

// Schema for the complete JSON import input
export const jsonMenuImportSchema = z.object({
  categories: z.array(aiImportCategorySchema).min(1),
  items: z.array(jsonImportItemSchema).min(1),
  modifierGroups: z.array(jsonImportModifierGroupSchema).optional(),
});

// Preview schemas for modifier groups
export const importPreviewModifierGroupSchema = z.object({
  name: z.string(),
  required: z.boolean(),
  minSelections: z.number(),
  maxSelections: z.number(),
  freeSelections: z.number().optional(),
  items: z.array(z.object({
    name: z.string(),
    priceOverride: z.number().optional(),
  })),
  assignTo: z.array(jsonImportAssignToEntrySchema),
  action: z.enum(['create', 'skip']),
});

// Extended preview schema that includes modifier groups
export const jsonImportPreviewSchema = aiImportPreviewSchema.extend({
  modifierGroups: z.array(importPreviewModifierGroupSchema).optional(),
});

// Extended confirm request schema that includes modifier groups + extra item fields
export const jsonImportConfirmRequestSchema = z.object({
  categories: z.array(z.object({
    name: z.string(),
    description: z.string().optional(),
    action: z.enum(['create', 'skip']),
    existingId: z.string().optional(),
  })),
  items: z.array(z.object({
    name: z.string(),
    description: z.string().optional(),
    price: z.number(),
    categoryName: z.string(),
    action: z.enum(['create', 'update', 'skip']),
    existingId: z.string().optional(),
    showInMenu: z.boolean().optional(),
    canBeModifier: z.boolean().optional(),
    availability: productAvailabilitySchema.optional(),
  })),
  modifierGroups: z.array(z.object({
    name: z.string(),
    required: z.boolean(),
    minSelections: z.number(),
    maxSelections: z.number(),
    freeSelections: z.number().optional(),
    items: z.array(z.object({
      name: z.string(),
      priceOverride: z.number().optional(),
    })),
    assignTo: z.array(jsonImportAssignToEntrySchema),
    action: z.enum(['create', 'skip']),
  })).optional(),
  replaceAll: z.boolean().optional(),
});

// Types inferred from schemas
export type ProductAvailabilityInput = z.infer<typeof productAvailabilitySchema>;
export type CreateMenuItemInput = z.infer<typeof createMenuItemSchema>;
export type UpdateMenuItemInput = z.infer<typeof updateMenuItemSchema>;
export type CreateMenuCategoryInput = z.infer<typeof createMenuCategorySchema>;
export type UpdateMenuCategoryInput = z.infer<typeof updateMenuCategorySchema>;
export type MenuItemOptionInput = z.infer<typeof menuItemOptionSchema>;
export type MenuItemOptionGroupInput = z.infer<typeof menuItemOptionGroupSchema>;
export type CreateModifierGroupInput = z.infer<typeof createModifierGroupSchema>;
export type UpdateModifierGroupInput = z.infer<typeof updateModifierGroupSchema>;
export type CreateModifierGroupItemInput = z.infer<typeof createModifierGroupItemSchema>;
export type UpdateModifierGroupItemInput = z.infer<typeof updateModifierGroupItemSchema>;
export type AssignModifierGroupInput = z.infer<typeof assignModifierGroupSchema>;

// AI Import types
export type AiImportCategory = z.infer<typeof aiImportCategorySchema>;
export type AiImportItem = z.infer<typeof aiImportItemSchema>;
export type AiImportResult = z.infer<typeof aiImportResultSchema>;
export type AiImportPreviewItem = z.infer<typeof aiImportPreviewItemSchema>;
export type AiImportPreviewCategory = z.infer<typeof aiImportPreviewCategorySchema>;
export type AiImportPreview = z.infer<typeof aiImportPreviewSchema>;
export type AiImportTextRequest = z.infer<typeof aiImportTextRequestSchema>;
export type AiImportConfirmRequest = z.infer<typeof aiImportConfirmRequestSchema>;

// JSON Import types
export type JsonImportAssignToEntry = z.infer<typeof jsonImportAssignToEntrySchema>;
export type JsonMenuImportInput = z.infer<typeof jsonMenuImportSchema>;
export type JsonImportItem = z.infer<typeof jsonImportItemSchema>;
export type JsonImportModifierGroup = z.infer<typeof jsonImportModifierGroupSchema>;
export type ImportPreviewModifierGroup = z.infer<typeof importPreviewModifierGroupSchema>;
export type JsonImportPreview = z.infer<typeof jsonImportPreviewSchema>;
export type JsonImportConfirmRequest = z.infer<typeof jsonImportConfirmRequestSchema>;

/**
 * Extract the product name from an assignTo entry (string or object).
 */
export function getAssignToName(entry: JsonImportAssignToEntry): string {
  return typeof entry === 'string' ? entry : entry.name;
}

/**
 * Extract per-product overrides from an assignTo entry.
 * Returns null values for string entries (no overrides).
 */
export function getAssignToOverrides(entry: JsonImportAssignToEntry): {
  requiredOverride: boolean | null;
  minSelectionsOverride: number | null;
  maxSelectionsOverride: number | null;
  freeSelectionsOverride: number | null;
} {
  if (typeof entry === 'string') {
    return { requiredOverride: null, minSelectionsOverride: null, maxSelectionsOverride: null, freeSelectionsOverride: null };
  }
  return {
    requiredOverride: entry.requiredOverride ?? null,
    minSelectionsOverride: entry.minSelectionsOverride ?? null,
    maxSelectionsOverride: entry.maxSelectionsOverride ?? null,
    freeSelectionsOverride: entry.freeSelectionsOverride ?? null,
  };
}

